import { showNotification } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteCookie } from 'cookies-next';
import { useRouter } from 'next/router';

import { IEmailVerificationRequest, ILoginRequest } from '@/types';
import { clearAuthCookies } from '@/utils/authCookies';
import { CONVERSATION_COOKIE_ID } from '@/utils/constants';
import onApiError from '@/utils/error';
import PageLink from '@/utils/PageLink';

import http, { ApiResponseError } from '../http';
import {
  getLoggedInUser,
  googleSignIn,
  loginWithEmail,
  resendEmailVerification,
  verifyEmail,
} from '../requests/auth.requests';

export const useLoginWithEmail = () =>
  useMutation({
    mutationFn: async (payload: ILoginRequest) => {
      const res = await loginWithEmail(payload);
      return res.data;
    },
    onError: (error: ApiResponseError) => {
      onApiError(error);
    },
  });

export const useGetLoggedInUser = () =>
  useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const res = await getLoggedInUser();
      return res.data.user;
    },
    retry: false,
  });

export const useGoogleSignIn = () =>
  useMutation({
    mutationFn: async (query: { id_token: string }) => {
      const res = await googleSignIn(query);
      return res.data;
    },
    onError: onApiError,
  });

export const useVerifyEmail = () =>
  useMutation({
    mutationFn: async (payload: IEmailVerificationRequest) => {
      const res = await verifyEmail(payload);
      return res.data;
    },
    onError: onApiError,
  });

export const useResendEmailVerification = () =>
  useMutation({
    mutationFn: async (email: string) => {
      const res = await resendEmailVerification(email);
      return res.data;
    },
    onError: onApiError,
  });

export const useDeleteMyAccount = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      const res = await http.delete(`/user/${userId}`, { reason });
      return res;
    },
    onSuccess: () => {
      showNotification({
        title: 'Account Deleted',
        message: 'Your account has been successfully deleted.',
        color: 'green',
      });

      clearAuthCookies();
      deleteCookie(CONVERSATION_COOKIE_ID);

      queryClient.clear();

      if (typeof window !== 'undefined') {
        sessionStorage.clear();
        localStorage.clear();
      }

      router.push(PageLink.login);
    },
    onError: (error: ApiResponseError) => {
      onApiError(error);
    },
  });
};

export const useSendDeleteOtp = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await http.post('/user/delete-otp');
      return res;
    },
    onSuccess: () => {
      showNotification({
        title: 'Verification Code Sent',
        message: 'A verification code has been sent to your email.',
        color: 'blue',
      });
    },
    onError: (error: ApiResponseError) => {
      onApiError(error);
    },
  });
};

export const useVerifyDeleteOtp = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ otp, reason }: { otp: string; reason: string }) => {
      const res = await http.post('/user/verify-delete-otp', { otp, reason });
      return res;
    },
    onSuccess: () => {
      showNotification({
        title: 'Account Scheduled for Deletion',
        message:
          'Your account has been scheduled for deletion. You have 30 days to restore it by logging in.',
        color: 'green',
      });

      clearAuthCookies();
      deleteCookie(CONVERSATION_COOKIE_ID);

      queryClient.clear();

      if (typeof window !== 'undefined') {
        sessionStorage.clear();
        localStorage.clear();
      }

      router.push(PageLink.login);
    },
    onError: (error: ApiResponseError) => {
      onApiError(error);
    },
  });
};

export const useRestoreAccount = () => {
  return useMutation({
    mutationFn: async ({ email, otp }: { email: string; otp: string }) => {
      const res = await http.post('/user/restore-account', { email, otp });
      return res;
    },
    onSuccess: () => {
      showNotification({
        title: 'Account Restored',
        message: 'Your account has been successfully restored.',
        color: 'green',
      });
    },
    onError: (error: ApiResponseError) => {
      onApiError(error);
    },
  });
};
